import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, Form, Spinner } from 'react-bootstrap';
import { PencilFill, CheckLg, XLg, PlusCircle, Trash } from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/SecurityContext';
import RoleGuard from '../security/RoleGuard';
import SectionControls from '../SectionControls';
import { updateNumberedStepsSection } from '../../services/contentService';
import './NumberedStepsBlock.scss';

function defaultStep() {
  return {
    title: 'Step title',
    description: 'One short sentence describing this step.',
  };
}

export function defaultNumberedStepsData() {
  return {
    title: 'How it works',
    steps: [defaultStep(), defaultStep(), defaultStep(), defaultStep()],
  };
}

// Read-only renderer. The step number is the 1-based index — we never store
// it, so the numbering can't get out of sync when admins reorder/remove steps.
export function NumberedStepsView({ data }) {
  const steps = Array.isArray(data?.steps) ? data.steps : [];
  return (
    <div className="numbered-steps-block">
      {data?.title ? (
        <h2 className="numbered-steps-block__title">{data.title}</h2>
      ) : null}
      <Row className="numbered-steps-block__steps g-4">
        {steps.map((step, idx) => (
          <Col key={idx} md={6} lg={3} className="numbered-steps-block__step-col">
            <div className="numbered-steps-block__step">
              <div className="numbered-steps-block__number">{idx + 1}</div>
              <div className="numbered-steps-block__step-title">{step.title}</div>
              <div className="numbered-steps-block__step-description">{step.description}</div>
            </div>
          </Col>
        ))}
      </Row>
    </div>
  );
}

function NumberedStepsBlock({
  pageId,
  section,
  onMoveUp,
  onMoveDown,
  onDelete,
  isFirstSection,
  isLastSection,
}) {
  const { user } = useAuth();
  const initialData = useMemo(() => section?.data || defaultNumberedStepsData(), [section]);
  const [data, setData] = useState(initialData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setData(section?.data || defaultNumberedStepsData());
  }, [section]);

  async function handleSave() {
    setIsSaving(true);
    try {
      const modifiedBy = user?.attributes?.email || user?.username || 'user';
      await updateNumberedStepsSection(pageId, section.position, data, modifiedBy);
      setIsEditing(false);
    } catch (e) {
      console.error('Error saving numbered steps:', e);
      // eslint-disable-next-line no-alert
      alert('Failed to save section. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel() {
    setData(section?.data || defaultNumberedStepsData());
    setIsEditing(false);
  }

  function updateTitle(value) {
    setData((prev) => ({ ...prev, title: value }));
  }

  function updateStep(idx, patch) {
    setData((prev) => {
      const next = { ...prev, steps: [...(prev.steps || [])] };
      next.steps[idx] = { ...next.steps[idx], ...patch };
      return next;
    });
  }

  function addStep() {
    setData((prev) => ({ ...prev, steps: [...(prev.steps || []), defaultStep()] }));
  }

  function removeStep(idx) {
    setData((prev) => {
      const next = { ...prev, steps: [...(prev.steps || [])] };
      next.steps.splice(idx, 1);
      return next;
    });
  }

  if (isEditing) {
    return (
      <div className="numbered-steps-block numbered-steps-block--editing">
        <Form.Group className="mb-3">
          <Form.Label>Section title</Form.Label>
          <Form.Control
            type="text"
            value={data.title || ''}
            onChange={(e) => updateTitle(e.target.value)}
          />
        </Form.Group>

        {(data.steps || []).map((step, idx) => (
          <div key={idx} className="numbered-steps-block__editor-step">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Step {idx + 1}</strong>
              <Button variant="outline-danger" size="sm" onClick={() => removeStep(idx)}>
                <Trash /> Remove
              </Button>
            </div>
            <Row className="g-2">
              <Col md={4}>
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  value={step.title || ''}
                  onChange={(e) => updateStep(idx, { title: e.target.value })}
                />
              </Col>
              <Col md={8}>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={step.description || ''}
                  onChange={(e) => updateStep(idx, { description: e.target.value })}
                />
              </Col>
            </Row>
          </div>
        ))}

        <Button variant="outline-primary" size="sm" onClick={addStep} className="mb-3">
          <PlusCircle /> Add step
        </Button>

        <div className="numbered-steps-block__editor-actions">
          <Button variant="success" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</>
            ) : (
              <><CheckLg /> Save</>
            )}
          </Button>
          <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
            <XLg /> Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="numbered-steps-block numbered-steps-block--view">
      <NumberedStepsView data={data} />
      <RoleGuard role="admin">
        <div className="admin-controls">
          <Button
            variant="outline-secondary"
            size="sm"
            className="edit-btn"
            onClick={() => setIsEditing(true)}
          >
            <PencilFill /> Edit steps
          </Button>
          <SectionControls
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            onDelete={onDelete}
            isFirstSection={isFirstSection}
            isLastSection={isLastSection}
          />
        </div>
      </RoleGuard>
    </div>
  );
}

export default NumberedStepsBlock;
